import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Spinner } from "reactstrap";
import { ACCURACY_TAGS } from "./NoteAccuracyPanel";
import NoteReviewReader from "./NoteReviewReader";
import { RatingRow, reviewableNotes } from "./FeedbackRatingsModal";

const PAGE_SIZE = 20;

// Section 06: the newest thumbs downs under the dashboard filters, a page at a
// time. Each opens its captured note and changes when there is one.
export default function FeedbackLatest({ apiClient, request }) {
  const [page, setPage] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [reviewIndex, setReviewIndex] = useState(null);
  const top = useRef(null);

  // New filters start from the newest again.
  useEffect(() => { setPage(0); }, [request]);

  useEffect(() => {
    let active = true;
    setError("");
    setReviewIndex(null);
    const offset = page * PAGE_SIZE;
    setResult(null);
    apiClient.JMPost("/v1/notes/feedback/ratings", { ...request, limit: PAGE_SIZE, offset }).then((response) => {
      if (!active) return;
      if (response?.error) throw new Error(response.error);
      setResult({ ...response, ratings: response?.ratings || [] });
    }).catch(() => { if (active) setError("Unable to load the latest thumbs downs. Try again."); });
    return () => { active = false; };
  }, [apiClient, request, page, retry]);

  const ratings = result?.ratings || [];
  const reviewable = reviewableNotes(ratings);
  const total = result?.total || 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const first = page * PAGE_SIZE + 1;
  const last = Math.min(total, (page + 1) * PAGE_SIZE);
  const go = (next) => {
    setPage(next);
    top.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
  };

  return (
    <div className="note-intel-panel fb-panel" ref={top}>
      <div className="note-intel-panel-header">
        <div>
          <h3>Latest thumbs downs</h3>
          <p>Newest first, under your filters. Open one to see exactly what happened.</p>
        </div>
        {!!total && <span className="fb-muted">{total.toLocaleString()} thumbs downs</span>}
      </div>
      {error && <Alert color="danger">{error} <Button size="sm" onClick={() => setRetry(retry + 1)}>Retry</Button></Alert>}
      {!result && !error && <div className="fb-loading"><Spinner size="sm" /> Loading thumbs downs…</div>}
      {result && !ratings.length && <p className="fb-muted">No thumbs downs in these filters.</p>}
      {ratings.map((rating) => (
        <RatingRow key={`${rating.conversationId}:${rating.runId}`} rating={rating} apiClient={apiClient}
          onReview={() => setReviewIndex(reviewable.findIndex((note) => note.noteId === rating.noteId))} />
      ))}
      {total > PAGE_SIZE && (
        <nav className="fb-pager" aria-label="Pages of thumbs downs">
          <Button size="sm" outline disabled={page === 0 || !result} onClick={() => go(page - 1)}>‹ Newer</Button>
          <span>{first.toLocaleString()}–{last.toLocaleString()} of {total.toLocaleString()} · page {page + 1} of {pages}</span>
          <Button size="sm" outline disabled={page + 1 >= pages || !result} onClick={() => go(page + 1)}>Older ›</Button>
        </nav>
      )}
      {reviewIndex !== null && reviewable[reviewIndex] && <NoteReviewReader
        apiClient={apiClient} note={reviewable[reviewIndex]} index={reviewIndex}
        count={reviewable.length} tagLabels={ACCURACY_TAGS}
        onClose={() => setReviewIndex(null)} onNavigate={setReviewIndex}
      />}
    </div>
  );
}
